// InstallEsimPage.jsx
import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaAndroid } from "react-icons/fa";
import { FiWifi } from "react-icons/fi";
import "./InstallEsimPage.css";
import { useLanguage } from "../i18n/LanguageContext";
import RichText from "../i18n/RichText";

// ===== Helpers =====
function getOSInfo() {
    const ua = navigator.userAgent || "";
// iOS (iPhone/iPad/iPod)
    if (/iPhone|iPad|iPod/i.test(ua)) {
        const m = ua.match(/OS\s(\d+(?:[_\.\d]+)*)/i);
        const version = m ? m[1].replace(/_/g, ".") : "";
        const parts = version.split(".");
        return { os: `iOS ${version}`, major: parseInt(parts[0] || 0), minor: parseInt(parts[1] || 0) };
    }
// Android
    if (/Android/i.test(ua)) {
        const m = ua.match(/Android\s([\d\.]+)/i);
        const version = m ? m[1] : "";
        return { os: `Android ${version}` };
    }
// macOS
    if (/Mac OS X/i.test(ua)) {
        const m = ua.match(/Mac OS X\s(\d+[_.\d]*)/i);
        const version = m ? m[1].replace(/_/g, ".") : "";
        return { os: `macOS ${version}` };
    }
// Windows
    if (/Windows NT/i.test(ua)) {
        const m = ua.match(/Windows NT\s([\d\.]+)/i);
        const version = m ? m[1] : "";
        return { os: `Windows ${version}` };
    }
    return { os: null }; // shown as install.unknownOs
}


function getUniversalLink(lpaString) {
    // Apple Universal Link for eSIM provisioning
    return `https://esimsetup.apple.com/esim_qrcode_provisioning?carddata=${encodeURIComponent(lpaString)}`;
}

export default function InstallEsimPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [tab, setTab] = useState("instant");
    const [copied, setCopied] = useState({1: false, 2: false, 3: false});

    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
    // Detect OS info here
    const osData = useMemo(() => getOSInfo(), []);
    const isIOS = Boolean(osData.os?.startsWith("iOS"));
    const isSupported = isIOS && (osData.major > 17 || (osData.major === 17 && osData.minor >= 4));


    const qrFromQuery = params.get("qrCodeUrl");
    const codeFromQuery = params.get("activationCode");
    const state = (location.state || {});

    const qrCodeUrl = qrFromQuery || state.qrCodeUrl || "";
    console.log(qrCodeUrl)
    const activationCode = codeFromQuery || state.activationCode || "";
    const activationCodeParts = activationCode.split("$");
    const smdpAddress = activationCodeParts[1]; // default for iPhone
    const iPhoneActivationCode = activationCodeParts[2];

    useEffect(() => {
        if (!isSupported) {
            setTab("qr");
        }
    }, [isSupported]);

    const copy = async (text, number) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(prev => ({ ...prev, [number]: true }));

            setTimeout(() => setCopied(false), 1600);
        } catch (e) {
            console.error(e);
            alert(t("install.copyFailed"));
        }
    };

    const handleInstantInstall = () => {
        const ua = navigator.userAgent || "";
        if (!/iPhone|iPad|iPod/i.test(ua)) {
            alert(t("install.iphoneOnly"));
            return;
        }
        const link = getUniversalLink(activationCode);
        const ok = window.confirm(t("install.confirm"));
        if (!ok) return;
        window.location.href = link;
    };



    return (
        <div className="install-page">
            {/* App Bar */}
            <header className="appbar">
                <button className="appbar-btn left" onClick={() => navigate(-1)} aria-label={t("common.back")}>❮</button>
                <h1 className="appbar-title">{t("install.appbar")}</h1>
            </header>

            {/* Tab Bar */}
            <nav className="tabbar" role="tablist" aria-label={t("install.tabsAria")}>
                <button className={`tab-pill ${tab === "instant" ? "active" : ""}`} onClick={() => setTab("instant")} role="tab"
                        aria-selected={tab === "instant"}>{t("install.tabs.instant")}
                </button>
                <button className={`tab-pill ${tab === "qr" ? "active" : ""}`} onClick={() => setTab("qr")} role="tab"
                        aria-selected={tab === "qr"}>{t("install.tabs.qr")}
                </button>
                <button className={`tab-pill ${tab === "manual" ? "active" : ""}`} onClick={() => setTab("manual")}
                        role="tab" aria-selected={tab === "manual"}>{t("install.tabs.manual")}
                </button>
            </nav>

            {/*
            <div className="success">
                <span>✅</span>
                <div>eSIM Anda siap dipasang.</div>
            </div>
            <div className="spacer"/>
            */}

            <div className="warn warn-inline">
                <span><FiWifi className="icon"/></span>
                <div>{t("install.wifiWarn")}</div>
            </div>


            {tab === "instant" && (
                <div className="instant">

                    {!isIOS && (
                        <p className="error-text">
                            <RichText text={t("install.notIos")} />
                        </p>
                    )}


                    {isIOS && !isSupported && (
                        <p className="error-text">
                            <RichText text={t("install.oldIos")} />
                        </p>
                    )}

                    <div className="device-info">
                        <div className="device-title">{t("install.detected")}</div>
                        <div className="device-os">{osData.os || t("install.unknownOs")}</div>
                    </div>


                    {isIOS && isSupported && (
                        <div>
                            <details className="card accent-left" open>
                                <summary> {t("install.step1")}</summary>
                                <div className="steps">
                                    <p>{t("install.instantStep")}</p>
                                </div>
                            </details>

                            <details className="card accent-left">
                                <summary> {t("install.step2")}</summary>
                                <div className="steps">
                                    <ul>
                                        <li><RichText text={t("install.roaming1")} /></li>
                                        <li><RichText text={t("install.roaming2")} /></li>
                                    </ul>
                                </div>
                            </details>

                            <div className="install-action-bar">
                                <button className="install-btn black" onClick={handleInstantInstall}>{t("install.installBtn")}</button>
                            </div>

                        </div>
                    )}
                </div>
            )}

            {tab === "qr" && (
                <div className="grid">
                    <div className="info">
                        <p className="lead">{t("install.qrLead1")} <br></br> {t("install.qrLead2")}</p>
                    </div>
                    <div className="qr-wrap">
                        <img src={qrCodeUrl} alt={t("install.qrAlt")} className="qr-img"/>
                    </div>

                    <details className="card accent-left" open>
                        <summary> {t("install.step1")}</summary>
                        <div className="steps">
                            <p><RichText text={t("install.qrIphone")} /></p>
                            <p><RichText text={t("install.qrAndroid")} /></p>
                        </div>
                    </details>

                    <details className="card accent-left">
                        <summary> {t("install.step2")}</summary>
                        <div className="steps">
                            <ul>
                                <li><RichText text={t("install.roaming1")} /></li>
                                <li><RichText text={t("install.roaming2")} /></li>
                            </ul>
                        </div>
                    </details>
                </div>
            )}

            {tab === "manual" && (
                <div className="manual">
                    {/* iPhone */}
                    <div className="platform-card">
                        <div className="platform-head">
                            <span className="platform-icon" role="img" aria-label="Apple"></span>
                            <h3 className="platform-title">iPhone</h3>
                        </div>

                        <div className="code-row">
                            <div className="label small">{t("install.smdp")}</div>
                            <div className="code-box">
                                <div className="value-scroll">
                                    <span className="value">{smdpAddress}</span>
                                </div>
                                <button className="copy-chip" onClick={() => copy(smdpAddress, 1)}>
                                    {copied[1] ? t("install.copied") : t("install.copy")}
                                </button>
                            </div>
                        </div>

                        <div className="code-row">
                            <div className="label small">{t("install.activationCode")}</div>
                            <div className="code-box">
                                <div className="value-scroll">
                                    {/* use short iPhone code (not full LPA) */}
                                    <span className="value">{iPhoneActivationCode}</span>
                                </div>
                                <button className="copy-chip" onClick={() => copy(iPhoneActivationCode, 2)}>
                                    {copied[2] ? t("install.copied") : t("install.copy")}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Android */}
                    <div className="platform-card">
                        <div className="platform-head">
                            <FaAndroid size={18} color="#3DDC84" />
                            <h3 className="platform-title">Android</h3>
                        </div>

                        <div className="code-row">
                            <div className="label small">{t("install.activationCode")}</div>
                            <div className="code-box">
                                <div className="value-scroll">
                                    {/* Android uses the full LPA string */}
                                    <span className="value">{activationCode}</span>
                                </div>
                                <button className="copy-chip" onClick={() => copy(activationCode, 3)}>
                                    {copied[3] ? t("install.copied") : t("install.copy")}
                                </button>
                            </div>
                        </div>
                    </div>

                    <details className="card accent-left" open>
                        <summary> {t("install.step1")}</summary>
                        <div className="steps">
                            <p><RichText text={t("install.manualIphone")} /></p>
                            <p><RichText text={t("install.manualAndroid")} /></p>
                        </div>
                    </details>

                    <details className="card accent-left">
                        <summary> {t("install.step2")}</summary>
                        <div className="steps">
                        <ul>
                                <li><RichText text={t("install.roaming1")} /></li>
                                <li><RichText text={t("install.roaming2")} /></li>
                            </ul>
                        </div>
                    </details>
                </div>
            )}
        </div>
    );
}

